import type {
  McpSdkServerConfigWithInstance,
  Options as ClaudeAgentOptions,
} from "@anthropic-ai/claude-agent-sdk";
import type { AgentAttachment } from "../../shared/messages.js";

export type AgentProvider = string;

export type AgentMetadata = { [key: string]: unknown };

/**
 * Stdio-based MCP server (spawns a subprocess).
 */
export interface McpStdioServerConfig {
  type: "stdio";
  command: string;
  args?: string[];
  env?: Record<string, string>;
}

/**
 * HTTP-based MCP server.
 */
export interface McpHttpServerConfig {
  type: "http";
  url: string;
  headers?: Record<string, string>;
  /**
   * When true, every tool from this MCP server is eagerly injected into
   * the model's system prompt (full name + description + JSON schema)
   * instead of being advertised by name only and deferred behind a
   * tool-search discovery step. Equivalent to the SDK's
   * `defer_loading: false`.
   *
   * Set this on the Appostle directive MCP — its trigger words (`>handoff`,
   * `>learn`, `>report`, `>todo`, etc.) only work when Claude has the schemas in
   * context at the moment it sees the user's message. Without it, the
   * model lists tools by name, decides the discovery hop isn't worth it
   * for a small task, and bypasses the toolbelt entirely.
   */
  alwaysLoad?: boolean;
}

/**
 * SSE-based MCP server (Server-Sent Events over HTTP).
 */
export interface McpSseServerConfig {
  type: "sse";
  url: string;
  headers?: Record<string, string>;
}

/**
 * In-process SDK MCP server. Holds a live `McpServer` instance — only meaningful
 * for the Claude provider (other providers cannot consume an in-memory MCP
 * server). The runner constructs these via `createSdkMcpServer` from the SDK
 * and hands them to the agent at spawn time. NOT serializable.
 */
export interface McpSdkServerConfig {
  type: "sdk";
  /** Server name as the SDK exposes it. Tool names become `mcp__<name>__<tool>`. */
  name: string;
  /** Live SDK server instance returned by `createSdkMcpServer`. */
  config: McpSdkServerConfigWithInstance;
}

/**
 * Canonical MCP server configuration.
 * Discriminated union by `type` field.
 * Each provider normalizes this to their expected format. Only the Claude
 * provider supports the `sdk` variant; other providers ignore it.
 */
export type McpServerConfig =
  | McpStdioServerConfig
  | McpHttpServerConfig
  | McpSseServerConfig
  | McpSdkServerConfig;

export type AgentMode = {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  colorTier?: string;
};

export type ProviderStatus = "ready" | "loading" | "error" | "unavailable";

export type AgentModelDefinition = {
  provider: AgentProvider;
  id: string;
  label: string;
  description?: string;
  isDefault?: boolean;
  metadata?: AgentMetadata;
  thinkingOptions?: AgentSelectOption[];
  defaultThinkingOptionId?: string;
  betas?: string[];
};

export type AgentSelectOption = {
  id: string;
  label: string;
  description?: string;
  isDefault?: boolean;
  metadata?: AgentMetadata;
};

export interface ProviderSnapshotEntry {
  provider: AgentProvider;
  status: ProviderStatus;
  error?: string;
  models?: AgentModelDefinition[];
  modes?: AgentMode[];
  fetchedAt?: string;
  label?: string;
  description?: string;
  defaultModeId?: string | null;
}

export type AgentFeatureToggle = {
  type: "toggle";
  id: string;
  label: string;
  description?: string;
  tooltip?: string;
  icon?: string;
  value: boolean;
};

export type AgentFeatureSelect = {
  type: "select";
  id: string;
  label: string;
  description?: string;
  tooltip?: string;
  icon?: string;
  value: string | null;
  options: AgentSelectOption[];
};

export type AgentFeature = AgentFeatureToggle | AgentFeatureSelect;

export type AgentCapabilityFlags = {
  supportsStreaming: boolean;
  supportsSessionPersistence: boolean;
  supportsDynamicModes: boolean;
  supportsMcpServers: boolean;
  supportsReasoningStream: boolean;
  supportsToolInvocations: boolean;
};

export type AgentPersistenceHandle = {
  provider: AgentProvider;
  sessionId: string;
  /** Provider specific handle (Codex thread id, Claude resume token, etc). */
  nativeHandle?: string;
  metadata?: AgentMetadata;
};

export type AgentPromptContentBlock =
  | { type: "text"; text: string }
  | { type: "image"; data: string; mimeType: string }
  | AgentAttachment;

export type AgentPromptInput = string | AgentPromptContentBlock[];

export type AgentRunOptions = {
  outputSchema?: unknown;
  resumeFrom?: AgentPersistenceHandle;
  maxThinkingTokens?: number;
};

export type RateLimitType =
  | "five_hour"
  | "seven_day"
  | "seven_day_opus"
  | "seven_day_sonnet"
  | "overage";

export interface AgentRateLimit {
  rateLimitType: RateLimitType;
  utilization: number;
  resetsAt?: number;
}

export type AgentUsage = {
  inputTokens?: number;
  cachedInputTokens?: number;
  outputTokens?: number;
  totalCostUsd?: number;
  contextWindowMaxTokens?: number;
  contextWindowUsedTokens?: number;
  rateLimits?: AgentRateLimit[];
};

export const TOOL_CALL_ICON_NAMES = [
  "wrench",
  "square_terminal",
  "eye",
  "pencil",
  "search",
  "bot",
  "sparkles",
  "brain",
  "mic_vocal",
] as const;

export type ToolCallIconName = (typeof TOOL_CALL_ICON_NAMES)[number];

export type ToolCallDetail =
  | {
      type: "shell";
      command: string;
      description?: string;
      cwd?: string;
      output?: string;
      exitCode?: number | null;
    }
  | {
      type: "read";
      filePath: string;
      content?: string;
      offset?: number;
      limit?: number;
    }
  | {
      type: "edit";
      filePath: string;
      oldString?: string;
      newString?: string;
      unifiedDiff?: string;
    }
  | {
      type: "write";
      filePath: string;
      content?: string;
    }
  | {
      type: "search";
      query: string;
      toolName?: "search" | "grep" | "glob" | "web_search";
      content?: string;
      filePaths?: string[];
      webResults?: Array<{
        title: string;
        url: string;
      }>;
      annotations?: string[];
      numFiles?: number;
      numMatches?: number;
      durationMs?: number;
      durationSeconds?: number;
      truncated?: boolean;
      mode?: "content" | "files_with_matches" | "count";
    }
  | {
      type: "fetch";
      url: string;
      prompt?: string;
      result?: string;
      code?: number;
      codeText?: string;
      bytes?: number;
      durationMs?: number;
    }
  | {
      type: "worktree_setup";
      worktreePath: string;
      branchName: string;
      log: string;
      commands: Array<{
        index: number;
        command: string;
        cwd: string;
        log: string;
        status: "running" | "completed" | "failed";
        exitCode: number | null;
        durationMs?: number;
      }>;
      truncated?: boolean;
    }
  | {
      type: "sub_agent";
      subAgentType?: string;
      description?: string;
      log?: string;
      actions?: Array<{
        index: number;
        toolName: string;
        summary?: string;
      }>;
    }
  | {
      type: "plain_text";
      label?: string;
      text?: string;
      icon?: ToolCallIconName;
    }
  | {
      type: "plan";
      text: string;
    }
  | {
      type: "image";
      toolName?: string;
      images: Array<{ mimeType: string; data: string }>;
    }
  | {
      type: "unknown";
      input: unknown | null;
      output: unknown | null;
    };

type ToolCallBase = {
  type: "tool_call";
  callId: string;
  name: string;
  detail: ToolCallDetail;
  metadata?: Record<string, unknown>;
};

type ToolCallRunningTimelineItem = ToolCallBase & {
  status: "running";
  error: null;
};

type ToolCallCompletedTimelineItem = ToolCallBase & {
  status: "completed";
  error: null;
};

type ToolCallFailedTimelineItem = ToolCallBase & {
  status: "failed";
  error: unknown;
};

type ToolCallCanceledTimelineItem = ToolCallBase & {
  status: "canceled";
  error: null;
};

export type ToolCallTimelineItem =
  | ToolCallRunningTimelineItem
  | ToolCallCompletedTimelineItem
  | ToolCallFailedTimelineItem
  | ToolCallCanceledTimelineItem;

export type CompactionTimelineItem = {
  type: "compaction";
  status: "loading" | "completed";
  trigger?: "auto" | "manual";
  preTokens?: number;
};

/**
 * Pointer to a daemon-stored attachment. When present, any client (including
 * a freshly-loaded one after Cmd-R or a phone that just connected) can fetch
 * bytes via the `fetch_attachment_bytes_request` RPC. The daemon is the
 * source of truth; local AttachmentStore copies are opportunistic caches.
 *
 * `relativePath` is workspace-relative POSIX:
 *   - images: `.appostle/tmp/uploaded_images/<filename>`
 *   - files:  `.appostle/tmp/uploaded_files/<id>__<name>`
 */
export type AttachmentDaemonRef = {
  kind: "image" | "file";
  attachmentId: string;
  relativePath: string;
};

export type UserMessageImageEcho = {
  id: string;
  mimeType: string;
  fileName?: string;
  byteSize?: number;
  daemonRef?: AttachmentDaemonRef;
};

export type UserMessageFileEcho = {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  daemonRef?: AttachmentDaemonRef;
};

export type AgentTimelineItem =
  | {
      type: "user_message";
      text: string;
      messageId?: string;
      /**
       * Real Claude transcript UUID for this turn, stamped on the row once the
       * Claude Agent SDK echoes the message back from the JSONL transcript.
       *
       * `messageId` is the daemon-/client-issued optimistic id (`msg_<ts>_<rand>`)
       * used for dedup, attachment correlation, and draft-store keys. The SDK
       * echo arrives later with its own UUID — instead of appending a duplicate
       * row, we stamp this field on the surviving optimistic row so fork/rewind
       * can resolve directly against the Claude transcript without falling
       * through to the fragile index-based lookup.
       */
      claudeUuid?: string;
      // Bytes-stripped chip metadata; populated when the daemon receives the
      // send and persisted on the timeline row so reload re-renders the chip
      // without round-tripping bytes.
      images?: UserMessageImageEcho[];
      files?: UserMessageFileEcho[];
      /**
       * True when `text` was synthesized by the daemon to satisfy non-empty
       * constraints (Anthropic's `cache_control` rejection of empty text
       * blocks) — e.g. "Sent 1 image" for an image-only send. The chat
       * bubble should NOT display this text; the chips already show what
       * the user attached. The text is preserved in the timeline only so
       * later turns replay non-empty content to the LLM SDK.
       */
      textIsSynthesized?: boolean;
    }
  | { type: "assistant_message"; text: string }
  | { type: "reasoning"; text: string }
  | ToolCallTimelineItem
  | { type: "todo"; items: { text: string; completed: boolean }[] }
  | { type: "error"; message: string }
  | CompactionTimelineItem;

export type AgentStreamEvent =
  | { type: "thread_started"; sessionId: string; provider: AgentProvider }
  | { type: "turn_started"; provider: AgentProvider; turnId?: string }
  | { type: "turn_completed"; provider: AgentProvider; usage?: AgentUsage; turnId?: string }
  | { type: "usage_updated"; provider: AgentProvider; usage: AgentUsage; turnId?: string }
  | {
      type: "turn_failed";
      provider: AgentProvider;
      error: string;
      code?: string;
      diagnostic?: string;
      turnId?: string;
    }
  | { type: "turn_canceled"; provider: AgentProvider; reason: string; turnId?: string }
  | { type: "timeline"; item: AgentTimelineItem; provider: AgentProvider; turnId?: string }
  | {
      type: "permission_requested";
      provider: AgentProvider;
      request: AgentPermissionRequest;
      turnId?: string;
    }
  | {
      type: "permission_resolved";
      provider: AgentProvider;
      requestId: string;
      resolution: AgentPermissionResponse;
      turnId?: string;
    }
  | {
      type: "attention_required";
      provider: AgentProvider;
      reason: "finished" | "error" | "permission";
      timestamp: string;
    }
  | {
      // Streams partial / final file content while the agent is writing a
      // file (Write / Edit / MultiEdit tool calls). Not persisted — purely
      // live-streaming to the UI so editors can render incremental progress.
      //
      // The final `tool_call` timeline item still captures the write as a
      // structural event; this channel exists alongside it to feed the live
      // editor widget.
      type: "file_chunk";
      provider: AgentProvider;
      toolUseId: string;
      filePath: string;
      // Full file content snapshot as of this chunk. We send full content
      // per chunk rather than deltas because file writes are small enough
      // that replacement cost is negligible and the client code stays
      // simpler (no offset bookkeeping). Revisit if we ever stream huge
      // files (log tails, etc.).
      content: string;
      isComplete: boolean;
      // Monotonic, per-toolUseId. Client discards out-of-order stale
      // chunks when a later sequenceId has already been applied.
      sequenceId: number;
      turnId?: string;
    };

export type AgentPermissionRequestKind = "tool" | "plan" | "question" | "mode" | "other";

export type AgentPermissionUpdate = AgentMetadata;

export type AgentPermissionAction = {
  id: string;
  label: string;
  behavior: "allow" | "deny";
  variant?: "primary" | "secondary" | "danger";
  intent?: "implement" | "implement_resume" | "dismiss";
};

export type AgentPermissionRequest = {
  id: string;
  provider: AgentProvider;
  name: string;
  kind: AgentPermissionRequestKind;
  title?: string;
  description?: string;
  input?: AgentMetadata;
  detail?: ToolCallDetail;
  suggestions?: AgentPermissionUpdate[];
  actions?: AgentPermissionAction[];
  metadata?: AgentMetadata;
};

export type AgentPermissionResponse =
  | {
      behavior: "allow";
      selectedActionId?: string;
      updatedInput?: AgentMetadata;
      updatedPermissions?: AgentPermissionUpdate[];
    }
  | {
      behavior: "deny";
      selectedActionId?: string;
      message?: string;
      interrupt?: boolean;
    };

export type AgentRunResult = {
  sessionId: string;
  finalText: string;
  usage?: AgentUsage;
  timeline: AgentTimelineItem[];
  canceled?: boolean;
};

export type AgentRuntimeInfo = {
  provider: AgentProvider;
  sessionId: string | null;
  model?: string | null;
  thinkingOptionId?: string | null;
  modeId?: string | null;
  extra?: AgentMetadata;
};

/**
 * Represents a slash command available in an agent session.
 * Commands are executed by sending them as prompts with / prefix.
 */
export type AgentSlashCommand = {
  name: string;
  description: string;
  argumentHint: string;
};

export type ListPersistedAgentsOptions = {
  limit?: number;
};

export type PersistedAgentDescriptor = {
  provider: AgentProvider;
  sessionId: string;
  cwd: string;
  title: string | null;
  lastActivityAt: Date;
  persistence: AgentPersistenceHandle;
  timeline: AgentTimelineItem[];
};

export type AgentSessionConfig = {
  provider: AgentProvider;
  cwd: string;
  /**
   * Provider-agnostic system/developer instruction string.
   * Mapped by each provider to its native instruction field.
   */
  systemPrompt?: string;
  allowedTools?: string[];
  modeId?: string;
  model?: string;
  thinkingOptionId?: string;
  featureValues?: Record<string, unknown>;
  title?: string | null;
  approvalPolicy?: string;
  sandboxMode?: string;
  networkAccess?: boolean;
  webSearch?: boolean;
  extra?: {
    codex?: AgentMetadata;
    claude?: Partial<ClaudeAgentOptions>;
  };
  mcpServers?: Record<string, McpServerConfig>;
  /**
   * Names of user MCP servers (non-appostle) that are allowed to connect for this agent.
   * Only relevant for OpenCode provider. Defaults to none (only appostle is on by default).
   */
  allowedMcpServers?: string[];
  /**
   * Internal agents are hidden from listings and don't trigger notifications.
   * They are used for ephemeral system tasks like commit/PR generation.
   */
  internal?: boolean;
};

export interface AgentLaunchContext {
  env?: Record<string, string>;
  chromeEnabled?: boolean;
  playwrightEnabled?: boolean;
}

/**
 * Returned by respondToPermission when the permission resolution requires
 * a follow-up turn (e.g. Codex plan approval → implementation).
 */
export interface AgentPermissionResult {
  followUpPrompt?: AgentPromptInput;
}

export interface AgentSession {
  readonly provider: AgentProvider;
  readonly id: string | null;
  readonly capabilities: AgentCapabilityFlags;
  readonly features?: AgentFeature[];
  run(prompt: AgentPromptInput, options?: AgentRunOptions): Promise<AgentRunResult>;
  startTurn(prompt: AgentPromptInput, options?: AgentRunOptions): Promise<{ turnId: string }>;
  subscribe(callback: (event: AgentStreamEvent) => void): () => void;
  streamHistory(): AsyncGenerator<AgentStreamEvent>;
  getRuntimeInfo(): Promise<AgentRuntimeInfo>;
  getAvailableModes(): Promise<AgentMode[]>;
  getCurrentMode(): Promise<string | null>;
  setMode(modeId: string): Promise<void>;
  getPendingPermissions(): AgentPermissionRequest[];
  respondToPermission(
    requestId: string,
    response: AgentPermissionResponse,
  ): Promise<AgentPermissionResult | void>;
  describePersistence(): AgentPersistenceHandle | null;
  interrupt(): Promise<void>;
  close(): Promise<void>;
  listCommands?(): Promise<AgentSlashCommand[]>;
  setModel?(modelId: string | null): Promise<void>;
  setThinkingOption?(thinkingOptionId: string | null): Promise<void>;
  setFeature?(featureId: string, value: unknown): Promise<void>;
  listMcpServers?(): Promise<Array<{ name: string; status: string }>>;
  connectMcpServer?(name: string): Promise<void>;
  disconnectMcpServer?(name: string): Promise<void>;
}

export interface ListModelsOptions {
  cwd?: string;
}

export interface ListModesOptions {
  cwd?: string;
}

export interface AgentClient {
  readonly provider: AgentProvider;
  readonly capabilities: AgentCapabilityFlags;
  createSession(
    config: AgentSessionConfig,
    launchContext?: AgentLaunchContext,
  ): Promise<AgentSession>;
  resumeSession(
    handle: AgentPersistenceHandle,
    overrides?: Partial<AgentSessionConfig>,
    launchContext?: AgentLaunchContext,
  ): Promise<AgentSession>;
  listModels(options?: ListModelsOptions): Promise<AgentModelDefinition[]>;
  listModes?(options?: ListModesOptions): Promise<AgentMode[]>;
  listPersistedAgents?(options?: ListPersistedAgentsOptions): Promise<PersistedAgentDescriptor[]>;
  /**
   * Check if this provider is available (CLI binary is installed).
   * Returns true if available, false otherwise.
   */
  isAvailable(): Promise<boolean>;
  getDiagnostic?(): Promise<{ diagnostic: string }>;
}
